import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, Download, Check } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_BOOKS = [
  {
    lookup_key: "schattenpsyche_band1",
    title: "Schattenpsyche · Band 1",
    subtitle: "Ein Profiler-Handbuch für den Alltag",
    genre: "Praktische Psychologie / Profiling",
    tag: "Dringend: Basis-Analyse",
    price: 19.99,
    currency: "eur",
    pages: 75,
    isbn: "978-3-00000-001-1",
    language: "Deutsch",
    format: "E-Book (PDF)",
    cover: "/covers/band1.png",
    synopsis:
      "Lies Menschen wie ein Profiler: Mimik, Körpersprache und die kleinen Signale im Alltag, verständlich erklärt und direkt anwendbar.",
    bundle: false,
  },
  {
    lookup_key: "schattenpsyche_trilogie",
    title: "Schattenpsyche · Die Trilogie",
    subtitle: "Band 1 bis 3 im Bundle",
    genre: "Praktische Psychologie / Profiling",
    tag: "Komplette Akte",
    price: 49.99,
    original_price: 59.97,
    currency: "eur",
    language: "Deutsch",
    format: "E-Book (PDF)",
    cover: "/covers/band1.png",
    synopsis:
      "Die komplette Akte: Band 1 sofort als Download, Band 2 und 3 folgen automatisch per E-Mail, sobald sie erscheinen.",
    bundle: true,
  },
];

const formatPrice = (price, currency) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(price);

const FeaturedBook = ({ book, onBuy }) => (
  <motion.article
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center glass rounded-2xl p-6 sm:p-10 lg:p-14"
    data-testid={`book-card-${book.lookup_key}`}
  >
    <div className="lg:col-span-5 relative">
      <div className="absolute -inset-8 bg-white/5 blur-3xl rounded-full pointer-events-none" />
      <div className="relative max-w-xs mx-auto group">
        <img
          src={book.cover}
          alt={`${book.title} Cover`}
          className="w-full drop-shadow-[0_40px_50px_rgba(0,0,0,0.85)] transition-transform duration-700 group-hover:scale-[1.04] group-hover:-rotate-1"
        />
      </div>
      <span className="stamp absolute -bottom-4 left-4 bg-void text-xs" data-testid={`book-tag-${book.lookup_key}`}>
        {book.tag}
      </span>
    </div>

    <div className="lg:col-span-7">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-400 mb-4">
        {book.genre}
      </p>
      <h3 className="font-cinzel text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
        {book.title}
      </h3>
      {book.subtitle && (
        <p className="font-typewriter text-lg sm:text-xl text-paper mt-2">{book.subtitle}</p>
      )}
      <div className="mt-4">
        <span className="stamp text-xs" data-testid={`book-band-${book.lookup_key}`}>
          Band 1 · Jetzt erhältlich
        </span>
      </div>
      <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-400 max-w-xl">
        {book.synopsis}
      </p>

      <p className="mt-8 font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
        Das lernst du
      </p>
      <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 max-w-xl" data-testid="book-benefit-list">
        {[
          "Die 5 wichtigsten Mimik-Signale sofort lesen",
          "Körpersprache & Mikroexpressionen sicher deuten",
          "Verbale Grenzen setzen: ruhig und bestimmt",
          "Manipulation erkennen, bevor sie wirkt",
        ].map((b) => (
          <li key={b} className="flex gap-3 items-start text-sm sm:text-base text-slate-300">
            <Check className="w-4 h-4 text-white mt-1 shrink-0" />
            {b}
          </li>
        ))}
      </ul>

      <dl className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl">
        {[
          ["Seiten", book.pages],
          ["Sprache", book.language],
          ["Format", book.format],
          ["ISBN", book.isbn],
        ].map(([k, v]) => (
          <div key={k} className="border border-white/10 rounded-lg px-3 py-2.5">
            <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{k}</dt>
            <dd className="font-typewriter text-xs sm:text-sm text-slate-200 mt-1 break-all">{v}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-10 flex flex-wrap items-center gap-6">
        <span
          className="font-cinzel text-4xl sm:text-5xl font-bold text-white"
          data-testid={`book-price-${book.lookup_key}`}
        >
          {formatPrice(book.price, book.currency)}
        </span>
        <button
          onClick={() => onBuy(book)}
          data-testid={`book-buy-button-${book.lookup_key}`}
          className="glow-btn min-h-[52px]"
        >
          <ShoppingBag className="w-4 h-4" />
          Jetzt Buch sichern
        </button>
      </div>
      <p className="mt-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
        <Download className="w-3.5 h-3.5" />
        Sofort-Download nach Zahlung, Link per E-Mail
      </p>
    </div>
  </motion.article>
);

const BundleCard = ({ bundle, onBuy }) => (
  <motion.article
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    className="relative glass rounded-2xl p-6 sm:p-10 lg:p-14 overflow-hidden"
    data-testid="bundle-card"
  >
    <span className="stamp absolute top-6 right-6 text-xs bg-void z-10" data-testid="bundle-savings-stamp">
      Spare 9,98 €
    </span>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-5 relative flex justify-center py-10 min-h-[300px]">
        <img
          src="/covers/band3.png"
          alt="Schattenpsyche Band 3 Cover"
          className="absolute w-32 sm:w-40 rotate-[8deg] translate-x-14 sm:translate-x-20 grayscale opacity-50 drop-shadow-[0_20px_30px_rgba(0,0,0,0.7)]"
        />
        <img
          src="/covers/band2.png"
          alt="Schattenpsyche Band 2 Cover"
          className="absolute w-32 sm:w-40 -rotate-[8deg] -translate-x-14 sm:-translate-x-20 grayscale opacity-50 drop-shadow-[0_20px_30px_rgba(0,0,0,0.7)]"
        />
        <img
          src="/covers/band1.png"
          alt="Schattenpsyche Band 1 Cover"
          className="relative w-40 sm:w-52 drop-shadow-[0_30px_50px_rgba(0,0,0,0.85)]"
        />
      </div>
      <div className="lg:col-span-7">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-400 mb-4">
          Die komplette Akte
        </p>
        <h3 className="font-cinzel text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
          Die Trilogie
        </h3>
        <p className="font-typewriter text-lg sm:text-xl text-paper mt-2">{bundle.subtitle}</p>
        <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-400 max-w-xl">
          {bundle.synopsis}
        </p>
        <ul className="mt-6 space-y-2.5 text-sm sm:text-base text-slate-300">
          <li className="flex gap-3"><span className="text-white">✦</span> Band 1 sofort als Download</li>
          <li className="flex gap-3"><span className="text-white">✦</span> Band 2 & 3 automatisch per E-Mail bei Erscheinen</li>
          <li className="flex gap-3"><span className="text-white">✦</span> Einmal zahlen, komplette Akte sichern</li>
        </ul>
        <div className="mt-10 flex flex-wrap items-center gap-6">
          <span className="font-cinzel text-4xl sm:text-5xl font-bold text-white" data-testid="bundle-price">
            {formatPrice(bundle.price, bundle.currency)}
          </span>
          <s className="font-mono text-xl text-slate-500">{formatPrice(bundle.original_price, bundle.currency)}</s>
          <button
            onClick={() => onBuy(bundle)}
            data-testid={`book-buy-button-${bundle.lookup_key}`}
            className="glow-btn min-h-[52px]"
          >
            <ShoppingBag className="w-4 h-4" />
            Bundle sichern
          </button>
        </div>
      </div>
    </div>
  </motion.article>
);

const SealedTeaser = ({ band, numeral, cover, date, product, released, onBuy }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8, delay: band * 0.1 }}
    className="relative rounded-xl overflow-hidden border border-white/10 min-h-[340px] flex items-end"
    data-testid={`book-teaser-band-${band}`}
  >
    <div className="absolute inset-0 flex items-center justify-center p-8">
      <img
        src={cover}
        alt={`Schattenpsyche Band ${numeral} Cover`}
        className={`max-h-full object-contain drop-shadow-[0_25px_40px_rgba(0,0,0,0.8)] transition-all duration-700 ${
          released ? "" : "grayscale opacity-30"
        }`}
      />
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-void via-void/70 to-transparent" />
    <div className="relative p-8 w-full text-center">
      <p className="font-typewriter uppercase tracking-[0.3em] text-slate-300 text-sm">
        Band {numeral}
      </p>
      {released && product ? (
        <>
          <span className="stamp mt-4 text-xs">Freigegeben</span>
          <p className="mt-4 font-cinzel text-2xl font-bold text-white" data-testid={`book-price-${product.lookup_key}`}>
            {formatPrice(product.price, product.currency)}
          </p>
          <button
            onClick={() => onBuy(product)}
            data-testid={`book-buy-button-${product.lookup_key}`}
            className="glow-btn mt-5 !py-3 !px-7 text-sm min-h-[48px]"
          >
            <ShoppingBag className="w-4 h-4" />
            Jetzt Buch sichern
          </button>
        </>
      ) : (
        <>
          <span className="stamp mt-4 text-xs">Versiegelt</span>
          <p className="mt-4 text-sm text-slate-400">
            Freigabe am {date}, zuerst auf TikTok.
          </p>
        </>
      )}
    </div>
  </motion.div>
);

export const BookShowcase = ({ onBuy }) => {
  const [books, setBooks] = useState(FALLBACK_BOOKS);
  const [released, setReleased] = useState({ band2: false, band3: false });

  useEffect(() => {
    axios
      .get(`${API}/books`)
      .then((res) => {
        if (res.data?.books?.length) setBooks(res.data.books);
        setReleased({ band2: !!res.data?.band2_released, band3: !!res.data?.band3_released });
      })
      .catch(() => {});
  }, []);

  const featured = books.find((b) => !b.bundle) || books[0];
  const bundle = books.find((b) => b.bundle);

  return (
    <section id="books" className="relative py-24 lg:py-36" data-testid="book-showcase-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14 lg:mb-20"
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-400 mb-4">
            Das Archiv
          </p>
          <h2 className="font-cinzel text-3xl sm:text-4xl lg:text-6xl font-bold text-white tracking-tight">
            Die Trilogie
          </h2>
        </motion.div>

        <div className="space-y-8" data-testid="book-showcase-grid">
          <FeaturedBook book={featured} onBuy={onBuy} />
          {bundle && <BundleCard bundle={bundle} onBuy={onBuy} />}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <SealedTeaser
              band={2}
              numeral="II"
              cover="/covers/band2.png"
              date="14.09.2026"
              product={books.find((b) => b.lookup_key === "schattenpsyche_band2")}
              released={released.band2}
              onBuy={onBuy}
            />
            <SealedTeaser
              band={3}
              numeral="III"
              cover="/covers/band3.png"
              date="01.12.2026"
              product={books.find((b) => b.lookup_key === "schattenpsyche_band3")}
              released={released.band3}
              onBuy={onBuy}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
